import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import type { UI2PickerFieldProps } from 'shared-libs/components/ui2/fields/ui2-picker-field/ui2-picker-field-props'
import { getDefaultPickerPlaceholder } from 'shared-libs/components/ui2/fields/ui2-picker-field/ui2-picker-field-props'
import {
  SelectOption,
  findOptionByValue,
  getEffectiveTranslationTable,
  getOptionLabel,
  resolveSelectOptions,
} from 'shared-libs/components/ui2/fields/core-field/selectable-field-props'

import { NativeSelect } from 'browser/components/atomic-elements/atoms/select/select/native-select'
import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import { UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'

import './_ui2-picker-field.scss'

interface IUI2PickerWebExtras {
  onChange?: (value: any) => void
  errors?: any[]
  className?: string
  isHorizontalLayout?: boolean
}

/**
 * Lightweight single-select built on the browser's native `<select>` on both DW
 * and MW — mobile browsers present that as the OS's own picker sheet, which is
 * the affordance users already know and gets accessibility, keyboard handling,
 * and scroll-position restore for free. Options come from `options`, or the
 * bound schema `enum`/`enumOptions`/`suggestions`.
 *
 * @uiComponent
 */
export interface IUI2PickerFieldProps extends UI2PickerFieldProps, IUI2PickerWebExtras {}

interface IUI2PickerFieldState {
  internalValue: any
}

export class UI2PickerField extends React.Component<IUI2PickerFieldProps, IUI2PickerFieldState> {
  private readonly hadInitialValue: boolean

  constructor(props: IUI2PickerFieldProps) {
    super(props)
    this.hadInitialValue = !_.isNil(props.value) && props.value !== ''
    this.state = { internalValue: props.value }
  }

  public UNSAFE_componentWillReceiveProps(next: IUI2PickerFieldProps) {
    if (next.value !== this.props.value) {
      this.setState({ internalValue: next.value })
    }
  }

  public render(): React.ReactNode {
    const { className, label, placeholder } = this.props
    const options = this.getTranslatedOptions()
    // `NativeSelect` renders this as the leading disabled <option>, which is
    // also what a valueless <select> displays — hence 'Select <label>' rather
    // than the bare label, which would read as a pickable option.
    const effectivePlaceholder = placeholder ?? getDefaultPickerPlaceholder(label)
    const effectiveEnablePrefilled = this.props.enablePrefilledMode && this.hadInitialValue

    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        enablePrefilledMode={effectiveEnablePrefilled}
        className={classNames('ui2-pickerField', className)}
        renderReadOnly={(_, helpers) => this.renderReadOnly(options, helpers)}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
      >
        <UI2FieldControl>{this.renderNativeSelect(options, effectivePlaceholder)}</UI2FieldControl>
      </UI2FieldShell>
    )
  }

  private renderNativeSelect(options: SelectOption[], placeholder: string): React.ReactNode {
    const { isDisabled, isHorizontalLayout, frames } = this.props
    return (
      <NativeSelect
        options={options}
        optionLabelPath='label'
        optionValuePath='value'
        value={this.state.internalValue}
        onChange={this.handleChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isHorizontalLayout={isHorizontalLayout}
        frames={frames}
      />
    )
  }

  private renderReadOnly(options: SelectOption[], { editNode }: { editNode?: React.ReactNode } = {}): React.ReactNode {
    const { value } = this.props
    const content = (_.isNil(value) || value === '')
      ? <UI2NoValueAvailable className='ui2-pickerField__readOnlyText' />
      : <span className='ui2-pickerField__readOnlyText'>{findOptionByValue(options, value)?.label ?? String(value)}</span>
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>{content}</div>
        {editNode}
      </>
    )
  }

  private getTranslatedOptions(): SelectOption[] {
    const translationTable = getEffectiveTranslationTable(this.props)
    return resolveSelectOptions(this.props).map((opt) => ({
      ...opt,
      label: getOptionLabel(opt, translationTable),
    }))
  }

  private handleChange = (newValue: any) => {
    this.setState({ internalValue: newValue })
    this.props.onChange?.(newValue)
  }
}
